import {
  Add,
  AddCircleOutline,
  AutoAwesomeOutlined,
  CheckCircleOutlineOutlined,
  DashboardOutlined,
  SaveOutlined,
  Settings,
} from '@mui/icons-material'
import { Stack, Box, Tooltip } from '@mui/material'
import { useRouter } from 'next/router'
import { useTranslation } from 'react-i18next'
import { track } from '@omnea-app/fe-analytics/useAnalytics'
import { hrefFor, type NavigableRouteKey } from '@omnea-app/fe-authz/routeHref'
import { useCanCreateRequest } from '@omnea-app/fe-authz/useCanCreateRequest'
import { useRouteAccess } from '@omnea-app/fe-authz/useRouteAccess'
import { InternalLinkButton } from '@omnea-app/fe-routing/InternalLinkButton'
import { InternalLinkIconButton } from '@omnea-app/fe-routing/InternalLinkIconButton'
import { isRouteActive } from '@omnea-app/fe-routing/routes/isRouteActive'
import { ExpandableSidebarLink } from './ExpandableSidebarLink'
import { SidebarLink } from './SidebarLink'
import { SidebarUncontrolledProps } from './SidebarUncontrolled'
import { SidebarList, SidebarBadge, SidebarDivider, SidebarItemWrapper } from './StyledSidebarComponents'

type AppSidebarContentProps = Omit<SidebarUncontrolledProps, 'path' | 'onToggleExpanded'>

const CREATE_REQUEST_HREF = hrefFor('startRequest')

export const AppSidebarContent = ({ isSidebarExpanded, items, numberOfActiveTasks }: AppSidebarContentProps) => {
  const { t } = useTranslation('common')
  const routeAccess = useRouteAccess()
  const canCreateRequest = useCanCreateRequest()

  const { asPath } = useRouter()
  const isSelected = (route: NavigableRouteKey) => isRouteActive(asPath, hrefFor(route))
  const trackCreateRequest = () => track('NewRequestInitiated', { source: 'RequestsCTA' })

  return (
    <Stack sx={{ height: '100%' }}>
      <Stack>
        <SidebarList>
          <SidebarLink
            href={hrefFor('dashboard')}
            title={t('sidebar.dashboard')}
            selected={isSelected('dashboard')}
            Icon={DashboardOutlined}
            isSidebarExpanded={isSidebarExpanded}
          />
          {routeAccess.get('ai') === 'allowed' ? (
            <SidebarLink
              href={hrefFor('ai')}
              title={t('sidebar.omnea-ai')}
              selected={isSelected('ai')}
              Icon={AutoAwesomeOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
          <SidebarLink
            href={hrefFor('tasks')}
            title={t('sidebar.tasks')}
            selected={isSelected('tasks')}
            Icon={CheckCircleOutlineOutlined}
            isSidebarExpanded={isSidebarExpanded}
          >
            {numberOfActiveTasks > 0 ? (
              <Tooltip title={t('sidebar.task-no-tooltip', { count: numberOfActiveTasks })}>
                <SidebarBadge sx={!isSidebarExpanded ? { ml: -3.5, mt: -3.5 } : {}}>{numberOfActiveTasks}</SidebarBadge>
              </Tooltip>
            ) : null}
          </SidebarLink>
          {canCreateRequest ? (
            <SidebarLink
              href={hrefFor('startDrafts')}
              title={t('sidebar.drafts')}
              selected={isSelected('startDrafts')}
              Icon={SaveOutlined}
              isSidebarExpanded={isSidebarExpanded}
            />
          ) : null}
        </SidebarList>
        <SidebarDivider />
        <SidebarList>
          {items.map((item) => {
            const { children, ...rest } = item
            if (children != null) {
              return <ExpandableSidebarLink {...rest} key={rest.href} items={children} isSidebarExpanded={isSidebarExpanded} />
            }
            return <SidebarLink {...rest} isSidebarExpanded={isSidebarExpanded} key={rest.href} />
          })}
        </SidebarList>
        <SidebarDivider />
      </Stack>
      <Stack sx={{ mt: 'auto' }}>
        <Box sx={{ backgroundColor: 'bg.surface.default' }}>
          <SidebarItemWrapper>
            <SidebarLink
              href={`${hrefFor('settingsNotifications')}?from=${encodeURIComponent(asPath)}`}
              title={t('sidebar.settings')}
              selected={isSelected('settingsNotifications')}
              Icon={Settings}
              isSidebarExpanded={isSidebarExpanded}
            />
          </SidebarItemWrapper>
          <SidebarDivider />
        </Box>
        {canCreateRequest ? (
          <Stack height={68} justifyContent="center" alignItems="center" sx={{ px: 3.5 }}>
            {isSidebarExpanded ? (
              <InternalLinkButton
                href={CREATE_REQUEST_HREF}
                color="primary"
                variant="contained"
                startIcon={<Add />}
                onClick={trackCreateRequest}
                sx={{ width: '100%' }}
              >
                {t('sidebar.create-request')}
              </InternalLinkButton>
            ) : (
              <InternalLinkIconButton href={CREATE_REQUEST_HREF} color="primary" onClick={trackCreateRequest}>
                <AddCircleOutline />
              </InternalLinkIconButton>
            )}
          </Stack>
        ) : null}
      </Stack>
    </Stack>
  )
}
